import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../../api/api";
import Swal from "sweetalert2";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AutoStories,
  Book,
  CalendarMonth,
  Category,
  CloudDownload,
  Description,
  FilterAlt,
  LibraryBooks,
  MenuBook,
  Search,
  School,
  Subject as SubjectIcon,
  Verified,
} from "@mui/icons-material";


const DOCUMENT_TYPES = {
  curriculum: "National Curriculum",
  syllabus: "Syllabus",
  teacher_guide: "Teacher Guide",
  framework: "Curriculum Framework",
  assessment: "Assessment Guide",
  other: "Other",
};


const getErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return (
      error?.message ||
      "An unexpected error occurred."
    );
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.detail) {
    return data.detail;
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join(", ")}`;
        }

        if (
          value &&
          typeof value === "object"
        ) {
          return `${key}: ${JSON.stringify(
            value
          )}`;
        }

        return `${key}: ${value}`;
      })
      .join("\n");
  }

  return "Unable to complete the request.";
};


const normalizeItems = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
};


const getCurriculumName = (document) => {
  return (
    document?.curriculum_label ||
    document?.curriculum_file_label ||
    document?.filename_label ||
    document?.filename_without_extension ||
    document?.title ||
    "Curriculum Document"
  );
};


const getAcademicYearName = (document) =>
  document?.academic_year_name ||
  document?.academic_year_data?.name ||
  document?.academic_year_data?.year ||
  document?.academic_year?.name ||
  document?.academic_year ||
  "Unknown Academic Year";


const getGradeName = (document) =>
  document?.grade_class_name ||
  document?.grade_class_data?.name ||
  document?.grade_class?.name ||
  "General Grade";


const getSubjectName = (document) =>
  document?.display_subject ||
  document?.subject_label ||
  document?.subject_name ||
  document?.subject_data?.name ||
  document?.subject?.name ||
  "General";


const formatDate = (value) => {
  if (!value) {
    return "Not specified";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
};


const formatFileSize = (bytes) => {
  if (!bytes) {
    return "0 MB";
  }

  return `${(
    Number(bytes) /
    (1024 * 1024)
  ).toFixed(2)} MB`;
};


const getBookColor = (index) => {
  const colors = [
    "#062A4B",
    "#063A6B",
    "#0F6DAB",
    "#5F6622",
    "#4FAE22",
    "#C0750D",
    "#8A5A20",
  ];

  return colors[index % colors.length];
};


const BookCard = ({
  document,
  index,
  onDownload,
}) => {
  const curriculumName =
    getCurriculumName(document);

  const subjectName =
    getSubjectName(document);

  const gradeName =
    getGradeName(document);

  const academicYear =
    getAcademicYearName(document);

  const bookColor =
    getBookColor(index);

  const fileName =
    document?.filename ||
    document?.file?.split("/")?.pop() ||
    curriculumName;

  return (
    <Box
      sx={{
        height: "100%",
        position: "relative",
        perspective: "1000px",

        "@keyframes bookFloat": {
          "0%, 100%": {
            transform:
              "translateY(0px) rotateX(0deg)",
          },
          "50%": {
            transform:
              "translateY(-5px) rotateX(1deg)",
          },
        },

        animation:
          "bookFloat 5s ease-in-out infinite",

        animationDelay: `${index * 80}ms`,
      }}
    >
      <Card
        sx={{
          height: "100%",
          minHeight: 455,
          borderRadius:
            "8px 12px 12px 8px",
          overflow: "visible",
          position: "relative",

          background:
            "linear-gradient(145deg,#fff,#f7f9fa)",

          border:
            "1px solid rgba(6,42,75,.10)",

          boxShadow:
            "0 15px 35px rgba(6,42,75,.13)",

          transition:
            "transform 220ms ease, box-shadow 220ms ease",

          "&:hover": {
            transform:
              "translateY(-8px)",
            boxShadow:
              "0 25px 45px rgba(6,42,75,.20)",
          },

          "&::before": {
            content: '""',
            position: "absolute",
            left: -7,
            top: 8,
            bottom: 8,
            width: 9,
            borderRadius:
              "5px 0 0 5px",

            background:
              `linear-gradient(180deg, ${bookColor}, #0A0F12)`,

            boxShadow:
              "inset -2px 0 rgba(255,255,255,.18)",
          },
        }}
      >
        <Box
          sx={{
            height: 175,
            position: "relative",
            borderRadius:
              "8px 12px 0 0",
            overflow: "hidden",

            background:
              `linear-gradient(145deg, ${bookColor}, #0A0F12)`,

            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",

            px: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 170,
              height: 170,
              border:
                "1px solid rgba(255,255,255,.10)",
              borderRadius: "50%",
              top: -80,
              right: -50,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 100,
              height: 100,
              border:
                "1px solid rgba(246,201,68,.14)",
              borderRadius: "50%",
              bottom: -55,
              left: -30,
            }}
          />

          <MenuBook
            sx={{
              color: "#F6C944",
              fontSize: 45,
              mb: 1,
              position: "relative",
            }}
          />

          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 17,
              lineHeight: 1.25,

              position: "relative",

              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {curriculumName}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color:
                "rgba(255,255,255,.72)",
              mt: 1,
              position: "relative",
            }}
          >
            {document.version
              ? `Version ${document.version}`
              : "Official Edition"}
          </Typography>
        </Box>

        <CardContent
          sx={{
            p: 2.2,
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <School
                sx={{
                  fontSize: 18,
                  color: "#0F6DAB",
                }}
              />

              <Typography
                variant="body2"
                fontWeight={700}
                color="#142536"
                noWrap
              >
                {gradeName}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <SubjectIcon
                sx={{
                  fontSize: 18,
                  color: "#4FAE22",
                }}
              />

              <Typography
                variant="body2"
                fontWeight={700}
                color="#142536"
                noWrap
              >
                {subjectName}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <CalendarMonth
                sx={{
                  fontSize: 18,
                  color: "#C0750D",
                }}
              />

              <Typography
                variant="body2"
                color="#53616c"
                noWrap
              >
                {academicYear}
              </Typography>
            </Stack>

            <Divider sx={{ my: 0.5 }} />

            <Stack
              direction="row"
              spacing={0.8}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                icon={<Verified />}
                label={
                  document.is_published
                    ? "Published"
                    : "Unpublished"
                }
                sx={{
                  background:
                    document.is_published
                      ? "rgba(79,174,34,.10)"
                      : "rgba(192,117,13,.10)",

                  color:
                    document.is_published
                      ? "#367c18"
                      : "#9a5b0a",

                  fontWeight: 700,
                }}
              />

              <Chip
                size="small"
                icon={<Category />}
                label={
                  DOCUMENT_TYPES[
                    document.document_type
                  ] ||
                  document.document_type ||
                  "Document"
                }
                sx={{
                  background:
                    "rgba(15,109,171,.08)",
                  color: "#0F5E92",
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="#6b7280"
              >
                {formatFileSize(
                  document.file_size
                )}
              </Typography>

              <Typography
                variant="caption"
                color="#6b7280"
              >
                {document.download_count || 0}{" "}
                downloads
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              color="#6b7280"
              sx={{
                minHeight: 34,

                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {document.description ||
                "Official curriculum resource available through the CAYEP digital library."}
            </Typography>

            <Tooltip
              title={fileName}
              placement="top"
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#53616c",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Description
                  sx={{
                    fontSize: 14,
                    verticalAlign: "middle",
                    mr: 0.5,
                  }}
                />

                {fileName}
              </Typography>
            </Tooltip>

            <Button
              fullWidth
              variant="contained"
              startIcon={<CloudDownload />}
              onClick={() =>
                onDownload(document)
              }
              sx={{
                mt: 0.5,
                borderRadius: 2,
                fontWeight: 800,

                background:
                  `linear-gradient(135deg, ${bookColor}, #062A4B)`,

                "&:hover": {
                  background:
                    "linear-gradient(135deg,#0F6DAB,#062A4B)",
                },
              }}
            >
              Download Curriculum
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};


const ViewCurriculums = () => {
  const [documents, setDocuments] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [gradeFilter, setGradeFilter] =
    useState("");

  const [subjectFilter, setSubjectFilter] =
    useState("");

  const [
    academicYearFilter,
    setAcademicYearFilter,
  ] = useState("");

  const [
    documentTypeFilter,
    setDocumentTypeFilter,
  ] = useState("");

  const [sortOrder, setSortOrder] =
    useState("newest");


  const loadCurriculums = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        "/national-curriculum/published/",
        {
          params: {
            page_size: 100,
          },
        }
      );

      setDocuments(
        normalizeItems(response)
      );
    } catch (requestError) {
      setError(
        getErrorMessage(requestError)
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadCurriculums();
  }, []);


  const gradeOptions = useMemo(() => {
    const values = documents.map(
      getGradeName
    );

    return [...new Set(values)]
      .filter(Boolean)
      .sort();
  }, [documents]);


  const subjectOptions = useMemo(() => {
    const values = documents.map(
      getSubjectName
    );

    return [...new Set(values)]
      .filter(Boolean)
      .sort();
  }, [documents]);


  const academicYearOptions = useMemo(() => {
    const values = documents.map(
      getAcademicYearName
    );

    return [...new Set(values)]
      .filter(Boolean)
      .sort()
      .reverse();
  }, [documents]);


  const filteredDocuments = useMemo(() => {
    const query =
      search.trim().toLowerCase();

    const result = documents.filter(
      (document) => {
        const curriculumName =
          getCurriculumName(
            document
          ).toLowerCase();

        const title =
          document.title?.toLowerCase() ||
          "";

        const filename =
          document.filename?.toLowerCase() ||
          "";

        const subject =
          getSubjectName(
            document
          ).toLowerCase();

        const grade =
          getGradeName(
            document
          ).toLowerCase();

        const description =
          document.description?.toLowerCase() ||
          "";

        const code =
          document.document_code?.toLowerCase() ||
          "";

        const matchesSearch =
          !query ||
          curriculumName.includes(
            query
          ) ||
          title.includes(query) ||
          filename.includes(query) ||
          subject.includes(query) ||
          grade.includes(query) ||
          description.includes(query) ||
          code.includes(query);

        const matchesGrade =
          !gradeFilter ||
          getGradeName(document) ===
            gradeFilter;

        const matchesSubject =
          !subjectFilter ||
          getSubjectName(document) ===
            subjectFilter;

        const matchesAcademicYear =
          !academicYearFilter ||
          getAcademicYearName(document) ===
            academicYearFilter;

        const matchesType =
          !documentTypeFilter ||
          document.document_type ===
            documentTypeFilter;

        return (
          matchesSearch &&
          matchesGrade &&
          matchesSubject &&
          matchesAcademicYear &&
          matchesType
        );
      }
    );

    return result.sort(
      (a, b) => {
        if (sortOrder === "title") {
          return getCurriculumName(
            a
          ).localeCompare(
            getCurriculumName(b)
          );
        }

        const first =
          new Date(
            a.date_uploaded ||
              a.date_updated ||
              0
          ).getTime();

        const second =
          new Date(
            b.date_uploaded ||
              b.date_updated ||
              0
          ).getTime();

        if (sortOrder === "oldest") {
          return first - second;
        }

        return second - first;
      }
    );
  }, [
    documents,
    search,
    gradeFilter,
    subjectFilter,
    academicYearFilter,
    documentTypeFilter,
    sortOrder,
  ]);


  const clearFilters = () => {
    setSearch("");
    setGradeFilter("");
    setSubjectFilter("");
    setAcademicYearFilter("");
    setDocumentTypeFilter("");
    setSortOrder("newest");
  };


  const handleDownload = async (
    document
  ) => {
    const curriculumName =
      getCurriculumName(document);

    const subject =
      getSubjectName(document);

    const grade =
      getGradeName(document);

    const academicYear =
      getAcademicYearName(document);

    const fileName =
      document?.filename ||
      curriculumName;

    const result = await Swal.fire({
      icon: "question",

      title: "Download Curriculum?",

      html: `
        <div style="text-align:left">
          <div style="
            font-size:17px;
            font-weight:800;
            color:#062A4B;
            margin-bottom:8px;
          ">
            ${curriculumName}
          </div>

          <div style="
            color:#53616c;
            margin-bottom:4px;
          ">
            <strong>Grade/Class:</strong>
            ${grade}
          </div>

          <div style="
            color:#53616c;
            margin-bottom:4px;
          ">
            <strong>Category:</strong>
            ${subject}
          </div>

          <div style="
            color:#53616c;
            margin-bottom:4px;
          ">
            <strong>Academic Year:</strong>
            ${academicYear}
          </div>

          <div style="
            color:#53616c;
            margin-bottom:4px;
          ">
            <strong>Version:</strong>
            ${document.version || "N/A"}
          </div>

          <div style="
            color:#53616c;
            margin-bottom:4px;
            word-break:break-word;
          ">
            <strong>File:</strong>
            ${fileName}
          </div>
        </div>

        <br />

        <span>
          Are you sure you want to download this curriculum?
        </span>
      `,

      showCancelButton: true,

      confirmButtonText:
        "Yes, Download",

      cancelButtonText:
        "No, Cancel",

      confirmButtonColor:
        "#062A4B",

      cancelButtonColor:
        "#6b7280",

      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    Swal.fire({
      title: "Preparing Download",

      text:
        "Please wait while the curriculum is being downloaded.",

      allowOutsideClick: false,

      allowEscapeKey: false,

      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await api.get(
        `/national-curriculum/${document.id}/download/`,
        {
          responseType: "blob",
        }
      );

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob(
              [response.data],
              {
                type:
                  response.headers?.[
                    "content-type"
                  ] ||
                  "application/octet-stream",
              }
            );

      const url =
        window.URL.createObjectURL(blob);

      const anchor =
        window.document.createElement(
          "a"
        );

      anchor.href = url;

      anchor.download =
        document.filename ||
        document.filename_without_extension ||
        curriculumName ||
        "curriculum-document";

      window.document.body.appendChild(
        anchor
      );

      anchor.click();

      anchor.remove();

      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",

        title: "Download Started",

        text:
          "The curriculum document has been downloaded.",

        timer: 1800,

        showConfirmButton: false,
      });

      setDocuments((previous) =>
        previous.map((item) =>
          item.id === document.id
            ? {
                ...item,
                download_count:
                  Number(
                    item.download_count || 0
                  ) + 1,
              }
            : item
        )
      );
    } catch (requestError) {
      Swal.fire({
        icon: "error",

        title: "Download Failed",

        text:
          getErrorMessage(
            requestError
          ),

        confirmButtonColor:
          "#062A4B",
      });
    }
  };


  return (
    <Box
      sx={{
        "--cayep-nav-navy": "#062A4B",
        "--cayep-nav-blue": "#063A6B",
        "--cayep-nav-black": "#0A0F12",
        "--cayep-nav-gold": "#F6C944",
        "--cayep-nav-yellow": "#EFA81C",
        "--cayep-nav-orange": "#C0750D",
        "--cayep-nav-green": "#4FAE22",
        "--cayep-nav-olive": "#5F6622",
        "--cayep-nav-sky": "#0F6DAB",
        "--cayep-nav-white": "#F0EDEA",
        "--cayep-nav-gray": "#C4C7C7",
        "--nav-white": "#ffffff",
        "--nav-text": "#142536",
        "--nav-muted": "#6b7280",
        "--nav-border": "#e7ebee",
        "--nav-soft": "#f7f9fa",
        "--nav-shadow":
          "0 12px 35px rgba(6, 42, 75, 0.10)",
        "--nav-transition": "220ms ease",

        minHeight: "100vh",

        background:
          "linear-gradient(135deg,#f7f9fa 0%,#edf2f4 100%)",

        px: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },

        py: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",

            background:
              "linear-gradient(135deg,#062A4B 0%,#063A6B 55%,#0A0F12 100%)",

            color: "#fff",

            mb: 3,

            boxShadow:
              "0 18px 45px rgba(6,42,75,.20)",

            "&::before": {
              content: '""',
              position: "absolute",
              width: 260,
              height: 260,
              border:
                "1px solid rgba(246,201,68,.13)",
              borderRadius: "50%",
              right: -80,
              top: -100,
            },

            "&::after": {
              content: '""',
              position: "absolute",
              width: 170,
              height: 170,
              border:
                "1px solid rgba(79,174,34,.12)",
              borderRadius: "50%",
              left: -60,
              bottom: -100,
            },
          }}
        >
          <Box
            sx={{
              p: {
                xs: 2.5,
                md: 4,
              },

              position: "relative",
              zIndex: 1,
            }}
          >
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                md: "center",
              }}
              spacing={2}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <AutoStories
                    sx={{
                      fontSize: {
                        xs: 42,
                        md: 52,
                      },

                      color: "#F6C944",
                    }}
                  />

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        letterSpacing:
                          "-1px",

                        fontSize: {
                          xs: "1.9rem",
                          md: "2.8rem",
                        },
                      }}
                    >
                      Curriculum Library
                    </Typography>

                    <Typography
                      sx={{
                        color:
                          "rgba(255,255,255,.72)",

                        mt: 0.5,

                        maxWidth: 720,
                      }}
                    >
                      Explore official curriculum
                      resources organized like a
                      professional digital library.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={1}
              >
                <Chip
                  icon={
                    <LibraryBooks />
                  }
                  label={`${documents.length} Curriculum`}
                  sx={{
                    background:
                      "rgba(255,255,255,.10)",

                    color: "#fff",

                    fontWeight: 800,

                    border:
                      "1px solid rgba(255,255,255,.15)",
                  }}
                />

                <Chip
                  icon={<Verified />}
                  label="Official Resources"
                  sx={{
                    background:
                      "rgba(79,174,34,.18)",

                    color: "#dff5d3",

                    fontWeight: 800,

                    border:
                      "1px solid rgba(79,174,34,.25)",
                  }}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>


        <Card
          sx={{
            borderRadius: 3,

            mb: 3,

            boxShadow:
              "0 12px 35px rgba(6,42,75,.10)",

            border:
              "1px solid rgba(6,42,75,.07)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={1.5}
            >
              <TextField
                fullWidth

                value={search}

                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }

                placeholder="Search by curriculum title, subject, grade/class, code or description..."

                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{
                          color: "#0F6DAB",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl
                sx={{
                  minWidth: {
                    xs: "100%",
                    md: 180,
                  },
                }}
              >
                <InputLabel>
                  Grade / Class
                </InputLabel>

                <Select
                  value={gradeFilter}
                  label="Grade / Class"

                  onChange={(event) =>
                    setGradeFilter(
                      event.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    All Grades
                  </MenuItem>

                  {gradeOptions.map(
                    (grade) => (
                      <MenuItem
                        key={grade}
                        value={grade}
                      >
                        {grade}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              <FormControl
                sx={{
                  minWidth: {
                    xs: "100%",
                    md: 180,
                  },
                }}
              >
                <InputLabel>
                  Subject
                </InputLabel>

                <Select
                  value={subjectFilter}
                  label="Subject"

                  onChange={(event) =>
                    setSubjectFilter(
                      event.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    All Subjects
                  </MenuItem>

                  {subjectOptions.map(
                    (subject) => (
                      <MenuItem
                        key={subject}
                        value={subject}
                      >
                        {subject}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              <FormControl
                sx={{
                  minWidth: {
                    xs: "100%",
                    md: 180,
                  },
                }}
              >
                <InputLabel>
                  Academic Year
                </InputLabel>

                <Select
                  value={
                    academicYearFilter
                  }
                  label="Academic Year"

                  onChange={(event) =>
                    setAcademicYearFilter(
                      event.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    All Years
                  </MenuItem>

                  {academicYearOptions.map(
                    (year) => (
                      <MenuItem
                        key={year}
                        value={year}
                      >
                        {year}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              <FormControl
                sx={{
                  minWidth: {
                    xs: "100%",
                    md: 160,
                  },
                }}
              >
                <InputLabel>
                  Sort
                </InputLabel>

                <Select
                  value={sortOrder}
                  label="Sort"

                  onChange={(event) =>
                    setSortOrder(
                      event.target.value
                    )
                  }
                >
                  <MenuItem value="newest">
                    Newest First
                  </MenuItem>

                  <MenuItem value="oldest">
                    Oldest First
                  </MenuItem>

                  <MenuItem value="title">
                    Title A–Z
                  </MenuItem>
                </Select>
              </FormControl>

              <Tooltip title="Clear filters">
                <Button
                  variant="outlined"

                  onClick={
                    clearFilters
                  }

                  sx={{
                    minWidth: 50,

                    borderRadius: 2,

                    color: "#062A4B",

                    borderColor:
                      "#062A4B",
                  }}
                >
                  <FilterAlt />
                </Button>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>


        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}


        {!loading &&
          !error &&
          filteredDocuments.length === 0 && (
            <Card
              sx={{
                borderRadius: 4,

                p: 6,

                textAlign: "center",

                boxShadow:
                  "0 12px 35px rgba(6,42,75,.08)",
              }}
            >
              <MenuBook
                sx={{
                  fontSize: 80,

                  color: "#C4C7C7",

                  mb: 1,
                }}
              />

              <Typography
                variant="h5"
                fontWeight={800}
                color="#062A4B"
              >
                No Curriculum Books Found
              </Typography>

              <Typography
                color="#6b7280"
                sx={{
                  mt: 1,
                }}
              >
                Try changing your search or
                library filters.
              </Typography>
            </Card>
          )}


        {loading ? (
          <Grid
            container
            spacing={3}
          >
            {Array.from({
              length: 8,
            }).map((_, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={index}
              >
                <Card
                  sx={{
                    borderRadius: 3,
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={175}
                  />

                  <CardContent>
                    <Skeleton width="80%" />

                    <Skeleton width="65%" />

                    <Skeleton width="70%" />

                    <Skeleton
                      height={45}
                      sx={{
                        mt: 2,
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <LibraryBooks
                sx={{
                  color: "#C0750D",
                }}
              />

              <Typography
                variant="h6"
                fontWeight={900}
                color="#062A4B"
              >
                Curriculum Shelves
              </Typography>

              <Typography
                variant="body2"
                color="#6b7280"
              >
                {filteredDocuments.length}{" "}
                curriculum
                {filteredDocuments.length ===
                1
                  ? ""
                  : "s"}{" "}
                available
              </Typography>
            </Box>

            <Box
              sx={{
                position: "relative",
                pb: 3,

                "&::after": {
                  content: '""',

                  display: "block",

                  height: 18,

                  mt: 1,

                  borderRadius:
                    "0 0 8px 8px",

                  background:
                    "linear-gradient(180deg,#8d5b28,#4e3018)",

                  boxShadow:
                    "0 8px 12px rgba(0,0,0,.20)",
                },
              }}
            >
              <Grid
                container
                spacing={3}
                alignItems="stretch"
              >
                {filteredDocuments.map(
                  (document, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={document.id}
                    >
                      <BookCard
                        document={
                          document
                        }

                        index={index}

                        onDownload={
                          handleDownload
                        }
                      />
                    </Grid>
                  )
                )}
              </Grid>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};


export default ViewCurriculums;





// import React, {
//   useEffect,
//   useMemo,
//   useState,
// } from "react";

// import api from "../../api/api";
// import Swal from "sweetalert2";

// import {
//   Alert,
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Chip,
//   CircularProgress,
//   Divider,
//   FormControl,
//   Grid,
//   InputAdornment,
//   InputLabel,
//   MenuItem,
//   Select,
//   Skeleton,
//   Stack,
//   TextField,
//   Tooltip,
//   Typography,
// } from "@mui/material";

// import {
//   AutoStories,
//   Book,
//   CalendarMonth,
//   Category,
//   CloudDownload,
//   Description,
//   FilterAlt,
//   LibraryBooks,
//   MenuBook,
//   Search,
//   School,
//   Subject as SubjectIcon,
//   Verified,
// } from "@mui/icons-material";


// const DOCUMENT_TYPES = {
//   curriculum: "National Curriculum",
//   syllabus: "Syllabus",
//   teacher_guide: "Teacher Guide",
//   framework: "Curriculum Framework",
//   assessment: "Assessment Guide",
//   other: "Other",
// };


// const getErrorMessage = (error) => {
//   const data = error?.response?.data;

//   if (!data) {
//     return (
//       error?.message ||
//       "An unexpected error occurred."
//     );
//   }

//   if (typeof data === "string") {
//     return data;
//   }

//   if (data.detail) {
//     return data.detail;
//   }

//   if (typeof data === "object") {
//     return Object.entries(data)
//       .map(([key, value]) => {
//         if (Array.isArray(value)) {
//           return `${key}: ${value.join(", ")}`;
//         }

//         return `${key}: ${value}`;
//       })
//       .join("\n");
//   }

//   return "Unable to complete the request.";
// };


// const normalizeItems = (response) => {
//   const data = response?.data;

//   if (Array.isArray(data)) {
//     return data;
//   }

//   if (Array.isArray(data?.results)) {
//     return data.results;
//   }

//   return [];
// };


// const getAcademicYearName = (document) =>
//   document?.academic_year_name ||
//   document?.academic_year_data?.name ||
//   document?.academic_year_data?.year ||
//   document?.academic_year ||
//   "Unknown Academic Year";


// const getGradeName = (document) =>
//   document?.grade_class_name ||
//   document?.grade_class_data?.name ||
//   document?.grade_class?.name ||
//   "General Grade";


// const getSubjectName = (document) =>
//   document?.subject_name ||
//   document?.subject_data?.name ||
//   document?.subject?.name ||
//   "General";


// const formatDate = (value) => {
//   if (!value) {
//     return "Not specified";
//   }

//   const date = new Date(value);

//   if (Number.isNaN(date.getTime())) {
//     return value;
//   }

//   return date.toLocaleDateString(
//     undefined,
//     {
//       year: "numeric",
//       month: "short",
//       day: "numeric",
//     }
//   );
// };


// const formatFileSize = (bytes) => {
//   if (!bytes) {
//     return "0 MB";
//   }

//   return `${(
//     Number(bytes) /
//     (1024 * 1024)
//   ).toFixed(2)} MB`;
// };


// const getBookColor = (index) => {
//   const colors = [
//     "#062A4B",
//     "#063A6B",
//     "#0F6DAB",
//     "#5F6622",
//     "#4FAE22",
//     "#C0750D",
//     "#8A5A20",
//   ];

//   return colors[index % colors.length];
// };


// const BookCard = ({
//   document,
//   index,
//   onDownload,
// }) => {
//   const subjectName =
//     getSubjectName(document);

//   const gradeName =
//     getGradeName(document);

//   const academicYear =
//     getAcademicYearName(document);

//   const bookColor =
//     getBookColor(index);

//   return (
//     <Box
//       sx={{
//         height: "100%",
//         position: "relative",
//         perspective: "1000px",
//         "@keyframes bookFloat": {
//           "0%, 100%": {
//             transform:
//               "translateY(0px) rotateX(0deg)",
//           },
//           "50%": {
//             transform:
//               "translateY(-5px) rotateX(1deg)",
//           },
//         },
//         animation:
//           "bookFloat 5s ease-in-out infinite",
//         animationDelay: `${index * 80}ms`,
//       }}
//     >
//       <Card
//         sx={{
//           height: "100%",
//           minHeight: 455,
//           borderRadius: "8px 12px 12px 8px",
//           overflow: "visible",
//           position: "relative",
//           background:
//             "linear-gradient(145deg,#fff,#f7f9fa)",
//           border:
//             "1px solid rgba(6,42,75,.10)",
//           boxShadow:
//             "0 15px 35px rgba(6,42,75,.13)",
//           transition:
//             "transform 220ms ease, box-shadow 220ms ease",
//           "&:hover": {
//             transform:
//               "translateY(-8px)",
//             boxShadow:
//               "0 25px 45px rgba(6,42,75,.20)",
//           },
//           "&::before": {
//             content: '""',
//             position: "absolute",
//             left: -7,
//             top: 8,
//             bottom: 8,
//             width: 9,
//             borderRadius: "5px 0 0 5px",
//             background:
//               `linear-gradient(180deg, ${bookColor}, #0A0F12)`,
//             boxShadow:
//               "inset -2px 0 rgba(255,255,255,.18)",
//           },
//         }}
//       >
//         <Box
//           sx={{
//             height: 175,
//             position: "relative",
//             borderRadius:
//               "8px 12px 0 0",
//             overflow: "hidden",
//             background:
//               `linear-gradient(145deg, ${bookColor}, #0A0F12)`,
//             display: "flex",
//             flexDirection: "column",
//             alignItems: "center",
//             justifyContent: "center",
//             px: 2,
//             textAlign: "center",
//           }}
//         >
//           <Box
//             sx={{
//               position: "absolute",
//               width: 170,
//               height: 170,
//               border:
//                 "1px solid rgba(255,255,255,.10)",
//               borderRadius: "50%",
//               top: -80,
//               right: -50,
//             }}
//           />

//           <Box
//             sx={{
//               position: "absolute",
//               width: 100,
//               height: 100,
//               border:
//                 "1px solid rgba(246,201,68,.14)",
//               borderRadius: "50%",
//               bottom: -55,
//               left: -30,
//             }}
//           />

//           <MenuBook
//             sx={{
//               color: "#F6C944",
//               fontSize: 45,
//               mb: 1,
//               position: "relative",
//             }}
//           />

//           <Typography
//             sx={{
//               color: "#fff",
//               fontWeight: 800,
//               fontSize: 17,
//               lineHeight: 1.25,
//               position: "relative",
//               display: "-webkit-box",
//               WebkitLineClamp: 3,
//               WebkitBoxOrient: "vertical",
//               overflow: "hidden",
//             }}
//           >
//             {document.title}
//           </Typography>

//           <Typography
//             variant="caption"
//             sx={{
//               color:
//                 "rgba(255,255,255,.72)",
//               mt: 1,
//               position: "relative",
//             }}
//           >
//             {document.version
//               ? `Version ${document.version}`
//               : "Official Edition"}
//           </Typography>
//         </Box>

//         <CardContent
//           sx={{
//             p: 2.2,
//           }}
//         >
//           <Stack spacing={1.25}>
//             <Stack
//               direction="row"
//               spacing={1}
//               alignItems="center"
//             >
//               <School
//                 sx={{
//                   fontSize: 18,
//                   color: "#0F6DAB",
//                 }}
//               />

//               <Typography
//                 variant="body2"
//                 fontWeight={700}
//                 color="#142536"
//                 noWrap
//               >
//                 {gradeName}
//               </Typography>
//             </Stack>

//             <Stack
//               direction="row"
//               spacing={1}
//               alignItems="center"
//             >
//               <SubjectIcon
//                 sx={{
//                   fontSize: 18,
//                   color: "#4FAE22",
//                 }}
//               />

//               <Typography
//                 variant="body2"
//                 fontWeight={700}
//                 color="#142536"
//                 noWrap
//               >
//                 {subjectName}
//               </Typography>
//             </Stack>

//             <Stack
//               direction="row"
//               spacing={1}
//               alignItems="center"
//             >
//               <CalendarMonth
//                 sx={{
//                   fontSize: 18,
//                   color: "#C0750D",
//                 }}
//               />

//               <Typography
//                 variant="body2"
//                 color="#53616c"
//                 noWrap
//               >
//                 {academicYear}
//               </Typography>
//             </Stack>

//             <Divider sx={{ my: 0.5 }} />

//             <Stack
//               direction="row"
//               spacing={0.8}
//               flexWrap="wrap"
//               useFlexGap
//             >
//               <Chip
//                 size="small"
//                 icon={<Verified />}
//                 label="Published"
//                 sx={{
//                   background:
//                     "rgba(79,174,34,.10)",
//                   color: "#367c18",
//                   fontWeight: 700,
//                 }}
//               />

//               <Chip
//                 size="small"
//                 icon={<Category />}
//                 label={
//                   DOCUMENT_TYPES[
//                     document.document_type
//                   ] ||
//                   document.document_type ||
//                   "Document"
//                 }
//                 sx={{
//                   background:
//                     "rgba(15,109,171,.08)",
//                   color: "#0F5E92",
//                   fontWeight: 700,
//                 }}
//               />
//             </Stack>

//             <Stack
//               direction="row"
//               justifyContent="space-between"
//               alignItems="center"
//               sx={{
//                 pt: 0.5,
//               }}
//             >
//               <Typography
//                 variant="caption"
//                 color="#6b7280"
//               >
//                 {formatFileSize(
//                   document.file_size
//                 )}
//               </Typography>

//               <Typography
//                 variant="caption"
//                 color="#6b7280"
//               >
//                 {document.download_count || 0} downloads
//               </Typography>
//             </Stack>

//             <Typography
//               variant="caption"
//               color="#6b7280"
//               sx={{
//                 minHeight: 34,
//                 display: "-webkit-box",
//                 WebkitLineClamp: 2,
//                 WebkitBoxOrient: "vertical",
//                 overflow: "hidden",
//               }}
//             >
//               {document.description ||
//                 "Official curriculum resource available through the CAYEP digital library."}
//             </Typography>

//             <Button
//               fullWidth
//               variant="contained"
//               startIcon={<CloudDownload />}
//               onClick={() =>
//                 onDownload(document)
//               }
//               sx={{
//                 mt: 0.5,
//                 borderRadius: 2,
//                 fontWeight: 800,
//                 background:
//                   `linear-gradient(135deg, ${bookColor}, #062A4B)`,
//                 "&:hover": {
//                   background:
//                     "linear-gradient(135deg,#0F6DAB,#062A4B)",
//                 },
//               }}
//             >
//               Download Curriculum
//             </Button>
//           </Stack>
//         </CardContent>
//       </Card>
//     </Box>
//   );
// };


// const ViewCurriculums = () => {
//   const [documents, setDocuments] =
//     useState([]);

//   const [loading, setLoading] =
//     useState(true);

//   const [error, setError] =
//     useState("");

//   const [search, setSearch] =
//     useState("");

//   const [gradeFilter, setGradeFilter] =
//     useState("");

//   const [subjectFilter, setSubjectFilter] =
//     useState("");

//   const [academicYearFilter, setAcademicYearFilter] =
//     useState("");

//   const [documentTypeFilter, setDocumentTypeFilter] =
//     useState("");

//   const [sortOrder, setSortOrder] =
//     useState("newest");


//   const loadCurriculums = async () => {
//     setLoading(true);
//     setError("");

//     try {
//       const response = await api.get(
//         "/national-curriculum/published/",
//         {
//           params: {
//             page_size: 100,
//           },
//         }
//       );

//       setDocuments(
//         normalizeItems(response)
//       );
//     } catch (requestError) {
//       setError(
//         getErrorMessage(requestError)
//       );
//     } finally {
//       setLoading(false);
//     }
//   };


//   useEffect(() => {
//     loadCurriculums();
//   }, []);


//   const gradeOptions = useMemo(() => {
//     const values = documents.map(
//       getGradeName
//     );

//     return [...new Set(values)]
//       .filter(Boolean)
//       .sort();
//   }, [documents]);


//   const subjectOptions = useMemo(() => {
//     const values = documents.map(
//       getSubjectName
//     );

//     return [...new Set(values)]
//       .filter(Boolean)
//       .sort();
//   }, [documents]);


//   const academicYearOptions = useMemo(() => {
//     const values = documents.map(
//       getAcademicYearName
//     );

//     return [...new Set(values)]
//       .filter(Boolean)
//       .sort()
//       .reverse();
//   }, [documents]);


//   const filteredDocuments = useMemo(() => {
//     const query =
//       search.trim().toLowerCase();

//     const result = documents.filter(
//       (document) => {
//         const title =
//           document.title?.toLowerCase() ||
//           "";

//         const subject =
//           getSubjectName(
//             document
//           ).toLowerCase();

//         const grade =
//           getGradeName(
//             document
//           ).toLowerCase();

//         const description =
//           document.description?.toLowerCase() ||
//           "";

//         const code =
//           document.document_code?.toLowerCase() ||
//           "";

//         const matchesSearch =
//           !query ||
//           title.includes(query) ||
//           subject.includes(query) ||
//           grade.includes(query) ||
//           description.includes(query) ||
//           code.includes(query);

//         const matchesGrade =
//           !gradeFilter ||
//           getGradeName(document) ===
//             gradeFilter;

//         const matchesSubject =
//           !subjectFilter ||
//           getSubjectName(document) ===
//             subjectFilter;

//         const matchesAcademicYear =
//           !academicYearFilter ||
//           getAcademicYearName(document) ===
//             academicYearFilter;

//         const matchesType =
//           !documentTypeFilter ||
//           document.document_type ===
//             documentTypeFilter;

//         return (
//           matchesSearch &&
//           matchesGrade &&
//           matchesSubject &&
//           matchesAcademicYear &&
//           matchesType
//         );
//       }
//     );

//     return result.sort(
//       (a, b) => {
//         const first =
//           new Date(
//             a.date_uploaded ||
//               a.date_updated ||
//               0
//           ).getTime();

//         const second =
//           new Date(
//             b.date_uploaded ||
//               b.date_updated ||
//               0
//           ).getTime();

//         if (sortOrder === "oldest") {
//           return first - second;
//         }

//         if (sortOrder === "title") {
//           return (
//             (a.title || "").localeCompare(
//               b.title || ""
//             )
//           );
//         }

//         return second - first;
//       }
//     );
//   }, [
//     documents,
//     search,
//     gradeFilter,
//     subjectFilter,
//     academicYearFilter,
//     documentTypeFilter,
//     sortOrder,
//   ]);


//   const clearFilters = () => {
//     setSearch("");
//     setGradeFilter("");
//     setSubjectFilter("");
//     setAcademicYearFilter("");
//     setDocumentTypeFilter("");
//     setSortOrder("newest");
//   };


//   const handleDownload = async (document) => {
//     const subject =
//       getSubjectName(document);

//     const grade =
//       getGradeName(document);

//     const academicYear =
//       getAcademicYearName(document);

//     const result = await Swal.fire({
//       icon: "question",
//       title: "Download Curriculum?",
//       html: `
//         <div style="text-align:left">
//           <strong>${document.title || "Curriculum"}</strong>
//           <br />
//           <span style="color:#6b7280">
//             ${grade} • ${subject}
//           </span>
//           <br />
//           <span style="color:#6b7280">
//             Academic Year: ${academicYear}
//           </span>
//           <br />
//           <span style="color:#6b7280">
//             Version: ${document.version || "N/A"}
//           </span>
//         </div>
//         <br />
//         <span>Are you sure you want to download this curriculum?</span>
//       `,
//       showCancelButton: true,
//       confirmButtonText: "Yes, Download",
//       cancelButtonText: "No, Cancel",
//       confirmButtonColor: "#062A4B",
//       cancelButtonColor: "#6b7280",
//       reverseButtons: true,
//     });

//     if (!result.isConfirmed) {
//       return;
//     }

//     Swal.fire({
//       title: "Preparing Download",
//       text: "Please wait while the curriculum is being downloaded.",
//       allowOutsideClick: false,
//       allowEscapeKey: false,
//       didOpen: () => {
//         Swal.showLoading();
//       },
//     });

//     try {
//       const response = await api.get(
//         `/national-curriculum/${document.id}/download/`,
//         {
//           responseType: "blob",
//         }
//       );

//       const blob =
//         response.data instanceof Blob
//           ? response.data
//           : new Blob(
//               [response.data],
//               {
//                 type:
//                   response.headers?.[
//                     "content-type"
//                   ] ||
//                   "application/octet-stream",
//               }
//             );

//       const url =
//         window.URL.createObjectURL(blob);

//       const anchor =
//         window.document.createElement("a");

//       anchor.href = url;

//       anchor.download =
//         document.filename ||
//         document.title ||
//         "curriculum-document";

//       window.document.body.appendChild(
//         anchor
//       );

//       anchor.click();

//       anchor.remove();

//       window.URL.revokeObjectURL(url);

//       Swal.fire({
//         icon: "success",
//         title: "Download Started",
//         text: "The curriculum document has been downloaded.",
//         timer: 1800,
//         showConfirmButton: false,
//       });

//       setDocuments((previous) =>
//         previous.map((item) =>
//           item.id === document.id
//             ? {
//                 ...item,
//                 download_count:
//                   Number(
//                     item.download_count || 0
//                   ) + 1,
//               }
//             : item
//         )
//       );
//     } catch (requestError) {
//       Swal.fire({
//         icon: "error",
//         title: "Download Failed",
//         text: getErrorMessage(
//           requestError
//         ),
//         confirmButtonColor: "#062A4B",
//       });
//     }
//   };


//   return (
//     <Box
//       sx={{
//         "--cayep-nav-navy": "#062A4B",
//         "--cayep-nav-blue": "#063A6B",
//         "--cayep-nav-black": "#0A0F12",
//         "--cayep-nav-gold": "#F6C944",
//         "--cayep-nav-yellow": "#EFA81C",
//         "--cayep-nav-orange": "#C0750D",
//         "--cayep-nav-green": "#4FAE22",
//         "--cayep-nav-olive": "#5F6622",
//         "--cayep-nav-sky": "#0F6DAB",
//         "--cayep-nav-white": "#F0EDEA",
//         "--cayep-nav-gray": "#C4C7C7",
//         "--nav-white": "#ffffff",
//         "--nav-text": "#142536",
//         "--nav-muted": "#6b7280",
//         "--nav-border": "#e7ebee",
//         "--nav-soft": "#f7f9fa",
//         "--nav-shadow":
//           "0 12px 35px rgba(6, 42, 75, 0.10)",
//         "--nav-transition": "220ms ease",

//         minHeight: "100vh",
//         background:
//           "linear-gradient(135deg,#f7f9fa 0%,#edf2f4 100%)",
//         px: {
//           xs: 1.5,
//           sm: 2,
//           md: 3,
//         },
//         py: {
//           xs: 2,
//           md: 3,
//         },
//       }}
//     >
//       <Box
//         sx={{
//           maxWidth: 1500,
//           mx: "auto",
//         }}
//       >
//         <Box
//           sx={{
//             borderRadius: 4,
//             overflow: "hidden",
//             position: "relative",
//             background:
//               "linear-gradient(135deg,#062A4B 0%,#063A6B 55%,#0A0F12 100%)",
//             color: "#fff",
//             mb: 3,
//             boxShadow:
//               "0 18px 45px rgba(6,42,75,.20)",
//             "&::before": {
//               content: '""',
//               position: "absolute",
//               width: 260,
//               height: 260,
//               border:
//                 "1px solid rgba(246,201,68,.13)",
//               borderRadius: "50%",
//               right: -80,
//               top: -100,
//             },
//             "&::after": {
//               content: '""',
//               position: "absolute",
//               width: 170,
//               height: 170,
//               border:
//                 "1px solid rgba(79,174,34,.12)",
//               borderRadius: "50%",
//               left: -60,
//               bottom: -100,
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: {
//                 xs: 2.5,
//                 md: 4,
//               },
//               position: "relative",
//               zIndex: 1,
//             }}
//           >
//             <Stack
//               direction={{
//                 xs: "column",
//                 md: "row",
//               }}
//               justifyContent="space-between"
//               alignItems={{
//                 xs: "flex-start",
//                 md: "center",
//               }}
//               spacing={2}
//             >
//               <Box>
//                 <Stack
//                   direction="row"
//                   spacing={1.5}
//                   alignItems="center"
//                 >
//                   <AutoStories
//                     sx={{
//                       fontSize: {
//                         xs: 42,
//                         md: 52,
//                       },
//                       color: "#F6C944",
//                     }}
//                   />

//                   <Box>
//                     <Typography
//                       variant="h3"
//                       sx={{
//                         fontWeight: 900,
//                         letterSpacing:
//                           "-1px",
//                         fontSize: {
//                           xs: "1.9rem",
//                           md: "2.8rem",
//                         },
//                       }}
//                     >
//                       Curriculum Library
//                     </Typography>

//                     <Typography
//                       sx={{
//                         color:
//                           "rgba(255,255,255,.72)",
//                         mt: 0.5,
//                         maxWidth: 720,
//                       }}
//                     >
//                       Explore official curriculum
//                       resources organized like a
//                       professional digital library.
//                     </Typography>
//                   </Box>
//                 </Stack>
//               </Box>

//               <Stack
//                 direction="row"
//                 spacing={1}
//               >
//                 <Chip
//                   icon={
//                     <LibraryBooks />
//                   }
//                   label={`${documents.length} Books`}
//                   sx={{
//                     background:
//                       "rgba(255,255,255,.10)",
//                     color: "#fff",
//                     fontWeight: 800,
//                     border:
//                       "1px solid rgba(255,255,255,.15)",
//                   }}
//                 />

//                 <Chip
//                   icon={<Verified />}
//                   label="Official Resources"
//                   sx={{
//                     background:
//                       "rgba(79,174,34,.18)",
//                     color: "#dff5d3",
//                     fontWeight: 800,
//                     border:
//                       "1px solid rgba(79,174,34,.25)",
//                   }}
//                 />
//               </Stack>
//             </Stack>
//           </Box>
//         </Box>


//         <Card
//           sx={{
//             borderRadius: 3,
//             mb: 3,
//             boxShadow:
//               "0 12px 35px rgba(6,42,75,.10)",
//             border:
//               "1px solid rgba(6,42,75,.07)",
//           }}
//         >
//           <CardContent sx={{ p: 2 }}>
//             <Stack
//               direction={{
//                 xs: "column",
//                 md: "row",
//               }}
//               spacing={1.5}
//             >
//               <TextField
//                 fullWidth
//                 value={search}
//                 onChange={(event) =>
//                   setSearch(
//                     event.target.value
//                   )
//                 }
//                 placeholder="Search by curriculum title, subject, grade/class, code or description..."
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <Search
//                         sx={{
//                           color: "#0F6DAB",
//                         }}
//                       />
//                     </InputAdornment>
//                   ),
//                 }}
//               />

//               <FormControl
//                 sx={{
//                   minWidth: {
//                     xs: "100%",
//                     md: 180,
//                   },
//                 }}
//               >
//                 <InputLabel>
//                   Grade / Class
//                 </InputLabel>

//                 <Select
//                   value={gradeFilter}
//                   label="Grade / Class"
//                   onChange={(event) =>
//                     setGradeFilter(
//                       event.target.value
//                     )
//                   }
//                 >
//                   <MenuItem value="">
//                     All Grades
//                   </MenuItem>

//                   {gradeOptions.map(
//                     (grade) => (
//                       <MenuItem
//                         key={grade}
//                         value={grade}
//                       >
//                         {grade}
//                       </MenuItem>
//                     )
//                   )}
//                 </Select>
//               </FormControl>

//               <FormControl
//                 sx={{
//                   minWidth: {
//                     xs: "100%",
//                     md: 180,
//                   },
//                 }}
//               >
//                 <InputLabel>
//                   Subject
//                 </InputLabel>

//                 <Select
//                   value={subjectFilter}
//                   label="Subject"
//                   onChange={(event) =>
//                     setSubjectFilter(
//                       event.target.value
//                     )
//                   }
//                 >
//                   <MenuItem value="">
//                     All Subjects
//                   </MenuItem>

//                   {subjectOptions.map(
//                     (subject) => (
//                       <MenuItem
//                         key={subject}
//                         value={subject}
//                       >
//                         {subject}
//                       </MenuItem>
//                     )
//                   )}
//                 </Select>
//               </FormControl>

//               <FormControl
//                 sx={{
//                   minWidth: {
//                     xs: "100%",
//                     md: 180,
//                   },
//                 }}
//               >
//                 <InputLabel>
//                   Academic Year
//                 </InputLabel>

//                 <Select
//                   value={
//                     academicYearFilter
//                   }
//                   label="Academic Year"
//                   onChange={(event) =>
//                     setAcademicYearFilter(
//                       event.target.value
//                     )
//                   }
//                 >
//                   <MenuItem value="">
//                     All Years
//                   </MenuItem>

//                   {academicYearOptions.map(
//                     (year) => (
//                       <MenuItem
//                         key={year}
//                         value={year}
//                       >
//                         {year}
//                       </MenuItem>
//                     )
//                   )}
//                 </Select>
//               </FormControl>

//               <FormControl
//                 sx={{
//                   minWidth: {
//                     xs: "100%",
//                     md: 160,
//                   },
//                 }}
//               >
//                 <InputLabel>
//                   Sort
//                 </InputLabel>

//                 <Select
//                   value={sortOrder}
//                   label="Sort"
//                   onChange={(event) =>
//                     setSortOrder(
//                       event.target.value
//                     )
//                   }
//                 >
//                   <MenuItem value="newest">
//                     Newest First
//                   </MenuItem>

//                   <MenuItem value="oldest">
//                     Oldest First
//                   </MenuItem>

//                   <MenuItem value="title">
//                     Title A–Z
//                   </MenuItem>
//                 </Select>
//               </FormControl>

//               <Tooltip title="Clear filters">
//                 <Button
//                   variant="outlined"
//                   onClick={
//                     clearFilters
//                   }
//                   sx={{
//                     minWidth: 50,
//                     borderRadius: 2,
//                     color: "#062A4B",
//                     borderColor:
//                       "#062A4B",
//                   }}
//                 >
//                   <FilterAlt />
//                 </Button>
//               </Tooltip>
//             </Stack>
//           </CardContent>
//         </Card>


//         {error && (
//           <Alert
//             severity="error"
//             sx={{
//               mb: 3,
//               borderRadius: 2,
//             }}
//           >
//             {error}
//           </Alert>
//         )}


//         {!loading &&
//           !error &&
//           filteredDocuments.length === 0 && (
//             <Card
//               sx={{
//                 borderRadius: 4,
//                 p: 6,
//                 textAlign: "center",
//                 boxShadow:
//                   "0 12px 35px rgba(6,42,75,.08)",
//               }}
//             >
//               <MenuBook
//                 sx={{
//                   fontSize: 80,
//                   color: "#C4C7C7",
//                   mb: 1,
//                 }}
//               />

//               <Typography
//                 variant="h5"
//                 fontWeight={800}
//                 color="#062A4B"
//               >
//                 No Curriculum Books Found
//               </Typography>

//               <Typography
//                 color="#6b7280"
//                 sx={{
//                   mt: 1,
//                 }}
//               >
//                 Try changing your search or
//                 library filters.
//               </Typography>
//             </Card>
//           )}


//         {loading ? (
//           <Grid
//             container
//             spacing={3}
//           >
//             {Array.from({
//               length: 8,
//             }).map((_, index) => (
//               <Grid
//                 item
//                 xs={12}
//                 sm={6}
//                 md={4}
//                 lg={3}
//                 key={index}
//               >
//                 <Card
//                   sx={{
//                     borderRadius: 3,
//                   }}
//                 >
//                   <Skeleton
//                     variant="rectangular"
//                     height={175}
//                   />

//                   <CardContent>
//                     <Skeleton width="80%" />
//                     <Skeleton width="65%" />
//                     <Skeleton width="70%" />
//                     <Skeleton
//                       height={45}
//                       sx={{ mt: 2 }}
//                     />
//                   </CardContent>
//                 </Card>
//               </Grid>
//             ))}
//           </Grid>
//         ) : (
//           <Box>
//             <Box
//               sx={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: 1,
//                 mb: 2,
//               }}
//             >
//               <LibraryBooks
//                 sx={{
//                   color: "#C0750D",
//                 }}
//               />

//               <Typography
//                 variant="h6"
//                 fontWeight={900}
//                 color="#062A4B"
//               >
//                 Curriculum Shelves
//               </Typography>

//               <Typography
//                 variant="body2"
//                 color="#6b7280"
//               >
//                 {filteredDocuments.length}{" "}
//                 curriculum
//                 {filteredDocuments.length ===
//                 1
//                   ? ""
//                   : "s"}{" "}
//                 available
//               </Typography>
//             </Box>

//             <Box
//               sx={{
//                 position: "relative",
//                 pb: 3,

//                 "&::after": {
//                   content: '""',
//                   display: "block",
//                   height: 18,
//                   mt: 1,
//                   borderRadius:
//                     "0 0 8px 8px",
//                   background:
//                     "linear-gradient(180deg,#8d5b28,#4e3018)",
//                   boxShadow:
//                     "0 8px 12px rgba(0,0,0,.20)",
//                 },
//               }}
//             >
//               <Grid
//                 container
//                 spacing={3}
//                 alignItems="stretch"
//               >
//                 {filteredDocuments.map(
//                   (document, index) => (
//                     <Grid
//                       item
//                       xs={12}
//                       sm={6}
//                       md={4}
//                       lg={3}
//                       key={document.id}
//                     >
//                       <BookCard
//                         document={document}
//                         index={index}
//                         onDownload={
//                           handleDownload
//                         }
//                       />
//                     </Grid>
//                   )
//                 )}
//               </Grid>
//             </Box>
//           </Box>
//         )}
//       </Box>
//     </Box>
//   );
// };


// export default ViewCurriculums;